import { Send01, Trash01 } from "@untitled-ui/icons-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "@/components/containers/Modal";
import Button from "@/components/forms/Button";
import EmojiPicker from "@/components/forms/EmojiPicker";
import Textarea from "@/components/forms/Textarea";
import FileTypeIcon from "@/components/icons/FileTypeIcon";
import Loading from "@/components/icons/Loading";
import { cn } from "@/lib/utils";
import { type Media } from "@/types";
import { useUploadFile } from "@/user/services";
import { convertBytesToReadable } from "@/utils/convertBytesToReadable";

type FileSendModalProps = {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSend: (caption: string, media: Media) => void;
};

export default function FileSendModal({
  open,
  file,
  onClose,
  onSend,
}: FileSendModalProps) {
  const { t } = useTranslation("messenger.user");
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState<Media | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const singleLineHeightRef = useRef<number | null>(null);
  const { mutateAsync: uploadFile } = useUploadFile("file");

  useEffect(() => {
    if (!open || !file) {
      setCaption("");
      setMedia(null);
      setUploading(false);
      setUploadError(false);
      setIsMultiline(false);
      singleLineHeightRef.current = null;
      return;
    }

    setMedia(null);
    setUploading(true);
    setUploadError(false);

    uploadFile(file)
      .then((uploaded) => {
        setMedia(uploaded);
        setUploading(false);
      })
      .catch(() => {
        setUploading(false);
        setUploadError(true);
      });
  }, [open, file]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSend = !uploading && media !== null;
  const sendButtonColor = canSend ? "primary" : "secondary-gray";

  const handleSend = () => {
    if (!canSend || !media) return;
    onSend(caption.trim(), media);
    setCaption("");
  };

  const handleEmojiSelect = (emoji: string) => {
    setCaption((prev) => prev + emoji);
    textareaRef.current?.focus();
  };

  const iconPositionClass = isMultiline
    ? "bottom-0"
    : "top-1/2 -translate-y-1/2";

  const ext = file?.name.split(".").pop() ?? "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t("Send File")}
      footer={
        <div className="border-t px-4 py-6">
          <div
            className={cn(
              "relative flex gap-3",
              isMultiline ? "items-end" : "items-center",
            )}
          >
            <div className="relative isolate flex-1">
              <div
                className={cn("absolute start-0 z-10 p-1", iconPositionClass)}
              >
                <EmojiPicker
                  onChange={handleEmojiSelect}
                  keepOpen
                  buttonClassName="hover:bg-transparent"
                  iconClassName="size-4 text-gray-400"
                />
              </div>
              <Textarea
                ref={textareaRef}
                placeholder={t("Message")}
                className="max-h-23 w-full ltr:pr-3.5 ltr:pl-10 rtl:pr-10 rtl:pl-3.5"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                visibleLine={1}
                autoHeight
                onAutoHeight={(height) => {
                  if (singleLineHeightRef.current === null) {
                    singleLineHeightRef.current = height;
                  }
                  setIsMultiline(height > singleLineHeightRef.current + 2);
                }}
              />
            </div>
            <Button
              color={sendButtonColor}
              square
              size="lg"
              onClick={handleSend}
            >
              <Send01 className="size-5 rtl:rotate-y-180" />
            </Button>
          </div>
        </div>
      }
    >
      {!file ? (
        <div className="flex items-center justify-center py-4 text-gray-500" />
      ) : (
        <div className="group flex items-center gap-2 rounded-lg p-3 hover:bg-gray-50">
          <FileTypeIcon size="md" extension={ext} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900">
              {file.name}
            </div>
            <div className="text-xs text-gray-500">
              {convertBytesToReadable(file.size)}
            </div>
          </div>
          {uploading && <Loading className="size-4 text-gray-400" />}
          {uploadError && (
            <span className="text-xs text-red-500">
              {t("Error", { ns: "core.shared" })}
            </span>
          )}
          <Button
            color="tertiary-gray"
            size="sm"
            square
            onClick={onClose}
            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          >
            <Trash01 className="size-5" />
          </Button>
        </div>
      )}
    </Modal>
  );
}
